import React, { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, ClipboardList, ScanLine, Ticket, Zap } from "lucide-react";
import { toast } from "react-toastify";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { onlyLotteryDigits, parseLotteryScanCode } from "../../../../utils/lotteryScanParser";

const todayInput = () => new Date().toISOString().split("T")[0];
const SKIP_BATCH_WARNING_KEY = "lotopsSkipBatchTicketWarning";
const ticketThemes = [
  { bg: "#6966b6", accent: "#343066", text: "#fff7ed", muted: "rgba(255, 247, 237, 0.78)", shadow: "rgba(30, 27, 75, 0.28)" },
  { bg: "#2563eb", accent: "#1e3a8a", text: "#eff6ff", muted: "rgba(239, 246, 255, 0.78)", shadow: "rgba(30, 58, 138, 0.28)" },
  { bg: "#7c3aed", accent: "#4c1d95", text: "#faf5ff", muted: "rgba(250, 245, 255, 0.78)", shadow: "rgba(76, 29, 149, 0.28)" },
  { bg: "#be123c", accent: "#881337", text: "#fff1f2", muted: "rgba(255, 241, 242, 0.78)", shadow: "rgba(136, 19, 55, 0.28)" },
  { bg: "#047857", accent: "#064e3b", text: "#ecfdf5", muted: "rgba(236, 253, 245, 0.78)", shadow: "rgba(6, 78, 59, 0.28)" },
  { bg: "#c2410c", accent: "#7c2d12", text: "#fff7ed", muted: "rgba(255, 247, 237, 0.78)", shadow: "rgba(124, 45, 18, 0.28)" },
  { bg: "#0f766e", accent: "#134e4a", text: "#f0fdfa", muted: "rgba(240, 253, 250, 0.78)", shadow: "rgba(19, 78, 74, 0.28)" },
  { bg: "#4338ca", accent: "#312e81", text: "#eef2ff", muted: "rgba(238, 242, 255, 0.78)", shadow: "rgba(49, 46, 129, 0.28)" },
  { bg: "#a21caf", accent: "#701a75", text: "#fdf4ff", muted: "rgba(253, 244, 255, 0.78)", shadow: "rgba(112, 26, 117, 0.28)" },
  { bg: "#0369a1", accent: "#0c4a6e", text: "#f0f9ff", muted: "rgba(240, 249, 255, 0.78)", shadow: "rgba(12, 74, 110, 0.28)" },
  { bg: "#15803d", accent: "#14532d", text: "#f0fdf4", muted: "rgba(240, 253, 244, 0.78)", shadow: "rgba(20, 83, 45, 0.28)" },
  { bg: "#b45309", accent: "#78350f", text: "#fffbeb", muted: "rgba(255, 251, 235, 0.78)", shadow: "rgba(120, 53, 15, 0.28)" },
  { bg: "#334155", accent: "#0f172a", text: "#f8fafc", muted: "rgba(248, 250, 252, 0.78)", shadow: "rgba(15, 23, 42, 0.28)" },
  { bg: "#0e7490", accent: "#164e63", text: "#ecfeff", muted: "rgba(236, 254, 255, 0.78)", shadow: "rgba(22, 78, 99, 0.28)" },
  { bg: "#9333ea", accent: "#581c87", text: "#faf5ff", muted: "rgba(250, 245, 255, 0.78)", shadow: "rgba(88, 28, 135, 0.28)" },
];

export const parseLotteryQrCode = parseLotteryScanCode;

const statusText = (book) => book?.status || "Not in inventory";
const toTicketNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

function LotteryOperationsTicketScan({
  className = "",
  games = [],
  books = [],
  onDataUpdate,
  onAddGame,
  onReceiveBook,
  onActivateBook,
}) {
  const { Post, _Get } = useApi();
  const [scanValue, setScanValue] = useState("");
  const [manual, setManual] = useState({ gameNo: "", bookNo: "", ticketNo: "" });
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [pendingSale, setPendingSale] = useState(null);
  const [isShiftActive, setIsShiftActive] = useState(null);
  const [ticketThemeIndex, setTicketThemeIndex] = useState(0);
  const lastThemeScanRef = useRef("");
  const [skipBatchWarning, setSkipBatchWarning] = useState(
    () => window.localStorage.getItem(SKIP_BATCH_WARNING_KEY) === "true",
  );

  const parsed = useMemo(() => parseLotteryQrCode(scanValue), [scanValue]);
  const manualGameNo = onlyLotteryDigits(manual.gameNo).slice(0, 3);
  const manualBookNo = onlyLotteryDigits(manual.bookNo).slice(0, 6);
  const manualTicketNo = onlyLotteryDigits(manual.ticketNo).slice(0, 3);
  const activeParsed = parsed.isComplete
    ? parsed
    : {
        raw: "",
        gameNo: manualGameNo,
        bookNo: manualBookNo,
        ticketNo: manualTicketNo,
        scanType: manualTicketNo ? "ticket" : "book",
        isComplete: manualGameNo.length === 3 && manualBookNo.length > 0,
      };

  const matchedGame = games.find((game) => String(game.game_no) === String(activeParsed.gameNo));
  const matchedBook = books.find(
    (book) =>
      String(book.game_no) === String(activeParsed.gameNo) &&
      String(book.book_no) === String(activeParsed.bookNo),
  );
  const activeTheme = ticketThemes[ticketThemeIndex % ticketThemes.length];
  const ticketPreviewStyle = {
    "--ticket-bg": activeTheme.bg,
    "--ticket-accent": activeTheme.accent,
    "--ticket-text": activeTheme.text,
    "--ticket-muted": activeTheme.muted,
    "--ticket-shadow": activeTheme.shadow,
  };
  const previewGameTitle = matchedGame?.game_name || matchedBook?.game_name || "TICKET";

  const resetScan = () => {
    setScanValue("");
    setManual({ gameNo: "", bookNo: "", ticketNo: "" });
  };

  const getActiveShift = async ({ showToast = true } = {}) => {
    try {
      const shift = await _Get("isShiftActive");
      if (
        !shift?.shift_id ||
        shift.message === "No active shift found. Displaying data from the latest shift of the day." ||
        shift.message === "No shifts available for today."
      ) {
        setIsShiftActive(false);
        if (showToast) toast.error("Please start a shift before scanning lottery tickets.");
        return null;
      }
      setIsShiftActive(true);
      return shift;
    } catch (error) {
      setIsShiftActive(false);
      if (showToast) toast.error("Please start a shift before scanning lottery tickets.");
      return null;
    }
  };

  useEffect(() => {
    getActiveShift({ showToast: false });
    // Run once. useApi returns fresh function identities on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activateShift = async () => {
    try {
      await Post("createShiftBusinessReportAndRegisters");
      toast.success("Shift activated. You can scan tickets now.");
      setIsShiftActive(true);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Unable to activate shift.");
    }
  };

  const openReceiveBook = () => {
    if (!activeParsed.gameNo || !activeParsed.bookNo) {
      toast.error("Scan or enter a valid game and book number first.");
      return;
    }

    if (!matchedGame) {
      toast.info("Game is not available yet. Add game details first, then receive this book.");
      onAddGame?.({
        game_no: activeParsed.gameNo,
        pending_book_no: activeParsed.bookNo,
        pending_scan_no: scanValue || `${activeParsed.gameNo}${activeParsed.bookNo}`,
      });
      resetScan();
      return;
    }

    onReceiveBook?.({
      inventory_date: todayInput(),
      game_no: activeParsed.gameNo,
      game_name: matchedGame?.game_name || "",
      lottery_game: matchedGame?.id || "",
      book_no: activeParsed.bookNo,
      scan_no: scanValue || `${activeParsed.gameNo}${activeParsed.bookNo}`,
    });
    resetScan();
  };

  const openActivateBook = () => {
    if (!activeParsed.gameNo || !activeParsed.bookNo) {
      toast.error("Scan or enter a valid game and book number first.");
      return;
    }

    onActivateBook?.({
      active_date: todayInput(),
      game_no: activeParsed.gameNo,
      book_no: activeParsed.bookNo,
      game_name: matchedGame?.game_name || matchedBook?.game_name || "",
    });
    resetScan();
  };

  const submitTicketSale = async (sale) => {
    const shift = await getActiveShift();
    if (!shift) return;

    try {
      setIsSubmitting(true);
      await Post("updateGameAndInventory", {
        shift_id: shift.shift_id,
        activated_book_shift: shift.shift_id,
        game_no: sale.gameNo,
        book_no: sale.bookNo,
        today_ticket_no: sale.ticketNo,
      });

      setLastResult({
        title: "Ticket sale recorded",
        detail: `Game ${sale.gameNo}, book ${sale.bookNo}, ticket ${sale.ticketNo}`,
      });
      toast.success("Ticket scanned successfully.");
      setPendingSale(null);
      resetScan();
      onDataUpdate?.();
    } catch (error) {
      toast.error(error?.response?.data?.error || error?.response?.data?.message || "Unable to scan ticket.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const recordTicketScan = async () => {
    if (!activeParsed.gameNo || !activeParsed.bookNo || !activeParsed.ticketNo) {
      toast.error("Ticket scan must include game number, book number and ticket number.");
      return;
    }

    if (!matchedBook) {
      toast.error(matchedGame ? "Book is not in inventory yet. Receive the book first." : "Game is not available yet. Add the game first.");
      openReceiveBook();
      return;
    }

    if (matchedBook.status !== "activated") {
      toast.error("Book is not active yet. Activate it before scanning ticket sales.");
      openActivateBook();
      return;
    }

    const scannedTicket = toTicketNumber(activeParsed.ticketNo);
    const lastSoldTicket = toTicketNumber(matchedBook.today_ticket_no);
    const quantitySold = Number(matchedBook.quantity_sold || 0);
    const saleMode = matchedGame?.ticket_sale_mode || "descending";

    if (scannedTicket === null) {
      toast.error("Ticket number is not valid.");
      return;
    }

    if (saleMode === "descending" && quantitySold > 0 && lastSoldTicket !== null && scannedTicket >= lastSoldTicket) {
      toast.error(`Ticket ${activeParsed.ticketNo} is already in the sold range for this book.`);
      setLastResult({
        title: "Already sold ticket",
        detail: `Last sold ticket is ${String(lastSoldTicket).padStart(3, "0")}. Next ticket must be ${String(Math.max(lastSoldTicket - 1, 0)).padStart(3, "0")} or less.`,
      });
      resetScan();
      return;
    }

    if (saleMode === "ascending" && quantitySold > 0 && lastSoldTicket !== null && scannedTicket <= lastSoldTicket) {
      toast.error(`Ticket ${activeParsed.ticketNo} is already in the sold range for this book.`);
      setLastResult({
        title: "Already sold ticket",
        detail: `Last sold ticket is ${String(lastSoldTicket).padStart(3, "0")}. Next ticket must be ${String(lastSoldTicket + 1).padStart(3, "0")} or higher.`,
      });
      resetScan();
      return;
    }

    const sale = {
      gameNo: activeParsed.gameNo,
      bookNo: activeParsed.bookNo,
      ticketNo: activeParsed.ticketNo,
      scannedTicket,
      lastSoldTicket,
      quantitySold,
      saleMode,
      batchCount:
        saleMode === "descending" && quantitySold > 0 && lastSoldTicket !== null
          ? lastSoldTicket - scannedTicket
          : saleMode === "ascending" && quantitySold > 0 && lastSoldTicket !== null
            ? scannedTicket - lastSoldTicket
            : 1,
    };

    if (saleMode !== "random" && sale.batchCount > 1 && !skipBatchWarning) {
      setPendingSale(sale);
      resetScan();
      return;
    }

    await submitTicketSale(sale);
  };

  const confirmPendingSale = async () => {
    if (!pendingSale) return;
    if (skipBatchWarning) {
      window.localStorage.setItem(SKIP_BATCH_WARNING_KEY, "true");
    }
    await submitTicketSale(pendingSale);
  };

  const handleSubmit = async () => {
    if (!activeParsed.isComplete) {
      toast.error("Scan a valid lottery QR code or enter game and book manually.");
      return;
    }

    if (activeParsed.scanType === "ticket") {
      await recordTicketScan();
      return;
    }

    if (!matchedBook) {
      openReceiveBook();
      resetScan();
      return;
    }

    if (matchedBook.status === "non activated") {
      openActivateBook();
      resetScan();
      return;
    }

    setLastResult({
      title: "Book found",
      detail: `Game ${activeParsed.gameNo}, book ${activeParsed.bookNo} is ${statusText(matchedBook)}.`,
    });
    resetScan();
  };

  const handleScanChange = (event) => {
    const nextValue = event.target.value;
    const nextParsed = parseLotteryQrCode(nextValue);
    const themeKey = `${nextParsed.gameNo}-${nextParsed.bookNo}-${nextParsed.ticketNo || "book"}`;
    if (nextParsed.isComplete && nextParsed.raw.length >= 10 && themeKey !== lastThemeScanRef.current) {
      lastThemeScanRef.current = themeKey;
      setTicketThemeIndex((index) => index + 1);
    }
    setScanValue(nextValue);
    setLastResult(null);
  };

  const handleScanKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className={`lotops-modern-scan ${className}`}>
      <style>{styles}</style>
      {isShiftActive !== true ? (
        <div className="lotops-shift-required">
          <div>
            <span className="lotops-modern-kicker"><Zap size={15} /> Shift Required</span>
            <h3>{isShiftActive === null ? "Checking shift status" : "Start shift to scan lottery tickets"}</h3>
            <p>{isShiftActive === null ? "Please wait while we verify active shift access." : "Lottery scanning is available only during an active shift."}</p>
          </div>
          {isShiftActive === false && (
            <button type="button" className="lotops-modern-primary" onClick={activateShift}>
              <Zap size={15} /> Start Shift
            </button>
          )}
        </div>
      ) : (
        <>
      <div className="lotops-modern-scan-head">
        <div>
          <span className="lotops-modern-kicker"><ScanLine size={15} /> Lottery Ticket Scan</span>
          <h3>Scan book or ticket QR</h3>
          <p>Reads game, book and ticket directly from the scanner response.</p>
        </div>
      </div>

      <div className="lotops-modern-scan-grid">
        <div className="lotops-modern-input-card">
          <label>Scanner input</label>
          <input
            autoComplete="off"
            inputMode="numeric"
            value={scanValue}
            onChange={handleScanChange}
            onKeyDown={handleScanKeyDown}
            placeholder="Click here and scan QR code"
          />
          <div className="lotops-modern-help">
            Ticket scan: <strong>4450160988020...</strong> gives game <strong>445</strong>, book <strong>160988</strong>, ticket <strong>020</strong>.
          </div>
        </div>

        <div className="lotops-modern-preview" style={ticketPreviewStyle}>
          <div className="lotops-ticket-stub">
            <span>{activeParsed.ticketNo || "---"}</span>
          </div>
          <div className="lotops-ticket-main">
            <div className="lotops-ticket-serial">★ GAME {activeParsed.gameNo || "---"} ★</div>
            <div className="lotops-ticket-number">
              {previewGameTitle}
            </div>
            <div className="lotops-ticket-serial">★ BOOK {activeParsed.bookNo || "------"} ★</div>
            <div className="lotops-ticket-meta">
              <div>
                <span>Game</span>
                <strong>{activeParsed.gameNo || "-"}</strong>
              </div>
              <div>
                <span>Book</span>
                <strong>{activeParsed.bookNo || "-"}</strong>
              </div>
              <div>
                <span>Ticket</span>
                <strong>{activeParsed.ticketNo || "-"}</strong>
              </div>
              <div>
                <span>Status</span>
                <strong>{statusText(matchedBook)}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lotops-modern-actions">
        <button type="button" className="lotops-modern-primary" onClick={handleSubmit} disabled={isSubmitting}>
          <CheckCircle2 size={16} />
          {isSubmitting ? "Saving..." : activeParsed.scanType === "ticket" ? "Record Ticket" : "Continue"}
        </button>
        <button type="button" className="lotops-modern-secondary" onClick={openReceiveBook}>
          <ClipboardList size={16} /> Receive Book
        </button>
        <button type="button" className="lotops-modern-secondary" onClick={openActivateBook}>
          <Ticket size={16} /> Activate Book
        </button>
        <button type="button" className="lotops-modern-link" onClick={() => setIsManualOpen((value) => !value)}>
          {isManualOpen ? "Hide manual" : "Manual entry"}
        </button>
      </div>

      {isManualOpen && (
        <div className="lotops-modern-manual">
          <input
            value={manual.gameNo}
            onChange={(event) => setManual((prev) => ({ ...prev, gameNo: onlyLotteryDigits(event.target.value).slice(0, 3) }))}
            placeholder="Game"
          />
          <input
            value={manual.bookNo}
            onChange={(event) => setManual((prev) => ({ ...prev, bookNo: onlyLotteryDigits(event.target.value).slice(0, 6) }))}
            placeholder="Book"
          />
          <input
            value={manual.ticketNo}
            onChange={(event) => setManual((prev) => ({ ...prev, ticketNo: onlyLotteryDigits(event.target.value).slice(0, 3) }))}
            placeholder="Ticket optional"
          />
        </div>
      )}

      {lastResult && (
        <div className="lotops-modern-result">
          <strong>{lastResult.title}</strong>
          <span>{lastResult.detail}</span>
        </div>
      )}

      {pendingSale && (
        <div className="lotops-confirm-overlay" role="dialog" aria-modal="true">
          <div className="lotops-confirm-modal">
            <h4>Confirm ticket range sale</h4>
            <p>
              Last sold ticket is <strong>{String(pendingSale.lastSoldTicket).padStart(3, "0")}</strong>. You are scanning{" "}
              <strong>{String(pendingSale.scannedTicket).padStart(3, "0")}</strong>, so this will sell{" "}
              <strong>{pendingSale.batchCount}</strong> tickets together.
            </p>
            <label className="lotops-confirm-check">
              <input
                type="checkbox"
                checked={skipBatchWarning}
                onChange={(event) => setSkipBatchWarning(event.target.checked)}
              />
              Do not show this warning again
            </label>
            <div className="lotops-confirm-actions">
              <button type="button" className="lotops-modern-secondary" onClick={() => setPendingSale(null)}>
                Cancel
              </button>
              <button type="button" className="lotops-modern-primary" onClick={confirmPendingSale} disabled={isSubmitting}>
                {isSubmitting ? "Selling..." : "Yes, sell tickets"}
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

const styles = `
  .lotops-modern-scan {
    background: #ffffff;
    border: 1px solid #dbeafe;
    padding: 18px;
  }

  .lotops-modern-scan-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 14px;
  }

  .lotops-modern-kicker {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #1d4ed8;
    background: #eff6ff;
    border-radius: 999px;
    padding: 7px 10px;
    font-size: 12px;
    font-weight: 950;
  }

  .lotops-modern-scan h3 {
    margin: 10px 0 4px;
    color: #0f172a;
    font-size: 20px;
    font-weight: 950;
  }

  .lotops-modern-scan p {
    margin: 0;
    color: #64748b;
    font-size: 13px;
    font-weight: 750;
  }

  .lotops-modern-shift,
  .lotops-modern-primary,
  .lotops-modern-secondary,
  .lotops-modern-link {
    border: none;
    border-radius: 999px;
    padding: 10px 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    cursor: pointer;
    font-weight: 950;
  }

  .lotops-modern-shift,
  .lotops-modern-secondary {
    background: #f8fafc;
    color: #334155;
    border: 1px solid #e2e8f0;
  }

  .lotops-modern-primary {
    background: #16a34a;
    color: #ffffff;
  }

  .lotops-modern-primary:disabled {
    opacity: 0.65;
    cursor: wait;
  }

  .lotops-modern-link {
    background: transparent;
    color: #2563eb;
  }

  .lotops-modern-scan-grid {
    display: grid;
    grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.8fr);
    gap: 14px;
  }

  .lotops-modern-input-card,
  .lotops-modern-preview,
  .lotops-modern-result,
  .lotops-modern-manual {
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    background: #f8fafc;
    padding: 13px;
  }

  .lotops-modern-input-card label {
    display: block;
    margin-bottom: 8px;
    color: #475569;
    font-size: 12px;
    font-weight: 950;
    text-transform: uppercase;
  }

  .lotops-modern-input-card input,
  .lotops-modern-manual input {
    width: 100%;
    height: 46px;
    border: 1px solid #cbd5e1;
    border-radius: 13px;
    padding: 0 13px;
    background: #ffffff;
    color: #0f172a;
    font-size: 15px;
    font-weight: 900;
    outline: none;
  }

  .lotops-modern-input-card input:focus,
  .lotops-modern-manual input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
  }

  .lotops-modern-help {
    margin-top: 9px;
    color: #64748b;
    font-size: 12px;
    font-weight: 750;
    line-height: 1.45;
  }

  .lotops-modern-preview {
    position: relative;
    overflow: visible;
    display: flex;
    min-height: 172px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background:
      linear-gradient(135deg, var(--ticket-bg) 0%, var(--ticket-bg) 56%, var(--ticket-accent) 100%);
    clip-path: polygon(
      0 32px, 24px 32px, 34px 0,
      calc(100% - 34px) 0, calc(100% - 24px) 32px, 100% 32px,
      100% calc(100% - 32px), calc(100% - 24px) calc(100% - 32px), calc(100% - 34px) 100%,
      34px 100%, 24px calc(100% - 32px), 0 calc(100% - 32px)
    );
    box-shadow: 0 16px 24px var(--ticket-shadow);
    color: var(--ticket-text);
  }

  .lotops-modern-preview::before,
  .lotops-modern-preview::after {
    content: "";
    position: absolute;
    inset: 14px 20px 14px 18px;
    border: 5px solid var(--ticket-accent);
    border-radius: 0;
    background:
      linear-gradient(135deg, transparent 0 20px, rgba(255,255,255,0.12) 21px 22px, transparent 23px),
      linear-gradient(-135deg, transparent 0 20px, rgba(255,255,255,0.12) 21px 22px, transparent 23px);
    pointer-events: none;
    z-index: 2;
  }

  .lotops-modern-preview::after {
    inset: auto;
    top: 0;
    bottom: 0;
    left: 96px;
    width: 0;
    border: 0;
    border-left: 3px dashed rgba(255, 255, 255, 0.9);
    background: none;
    z-index: 4;
  }

  .lotops-ticket-main {
    position: relative;
    flex: 1;
    min-width: 0;
    display: grid;
    align-content: center;
    gap: 8px;
    padding: 22px 22px 18px;
    margin-left: 8px;
    background:
      radial-gradient(circle at 20% 35%, rgba(255,255,255,0.08), transparent 28%),
      linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0));
    text-align: center;
    z-index: 3;
  }

  .lotops-ticket-main::after {
    content: "";
    position: absolute;
    left: 26px;
    right: 26px;
    top: 50%;
    border-top: 2px solid var(--ticket-accent);
    transform: translateY(-50%);
  }

  .lotops-ticket-main::before {
    content: "";
    position: absolute;
    top: 31px;
    right: -5px;
    width: 12px;
    height: calc(100% - 62px);
    background:
      radial-gradient(circle at 50% 7px, #f8fafc 0 5px, transparent 6px) 0 0 / 12px 22px repeat-y;
    z-index: 4;
  }

  .lotops-ticket-serial {
    position: relative;
    z-index: 2;
    color: var(--ticket-text);
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(20px, 3vw, 34px);
    font-weight: 950;
    letter-spacing: 0.08em;
    line-height: 1;
    text-shadow: 0 2px 0 var(--ticket-shadow);
  }

  .lotops-ticket-number {
    position: relative;
    z-index: 2;
    margin: 2px 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(44px, 7vw, 78px);
    line-height: 0.9;
    font-weight: 950;
    letter-spacing: 0.01em;
    color: var(--ticket-accent);
    text-shadow:
      0 2px 0 rgba(255, 255, 255, 0.09),
      0 -1px 0 rgba(17, 24, 39, 0.18);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lotops-ticket-meta {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 6px;
    margin-top: 4px;
  }

  .lotops-ticket-meta div {
    min-width: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.26);
    padding-top: 5px;
  }

  .lotops-ticket-meta span,
  .lotops-ticket-stub span {
    display: block;
    color: var(--ticket-muted);
    font-size: 10px;
    font-weight: 950;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .lotops-ticket-meta strong,
  .lotops-ticket-stub strong {
    display: block;
    overflow: hidden;
    color: var(--ticket-text);
    font-size: 13px;
    font-weight: 950;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .lotops-ticket-stub {
    position: relative;
    width: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: 18px 8px 18px 15px;
    border-left: 0;
    background:
      linear-gradient(135deg, rgba(255,255,255,0.1), transparent),
      var(--ticket-bg);
    color: var(--ticket-text);
    text-align: center;
    z-index: 3;
  }

  .lotops-ticket-stub::before,
  .lotops-ticket-stub::after {
    content: "";
    position: absolute;
    left: -9px;
    width: 18px;
    height: calc(100% - 54px);
    top: 27px;
    background:
      radial-gradient(circle at 50% 8px, #f8fafc 0 8px, transparent 9px) 0 0 / 18px 26px repeat-y;
  }

  .lotops-ticket-stub::after {
    display: none;
  }

  .lotops-ticket-stub span {
    writing-mode: vertical-rl;
    transform: rotate(180deg);
    color: var(--ticket-text);
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(24px, 3.2vw, 34px);
    line-height: 1;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .lotops-ticket-barcode {
    width: 40px;
    height: 108px;
    border: 3px solid rgba(255,255,255,0.92);
    background:
      repeating-linear-gradient(90deg, #111827 0 2px, transparent 2px 4px, #111827 4px 7px, transparent 7px 10px);
  }

  @media (max-width: 520px) {
    .lotops-modern-preview {
      min-height: 148px;
      clip-path: polygon(
        0 24px, 18px 24px, 26px 0,
        calc(100% - 26px) 0, calc(100% - 18px) 24px, 100% 24px,
        100% calc(100% - 24px), calc(100% - 18px) calc(100% - 24px), calc(100% - 26px) 100%,
        26px 100%, 18px calc(100% - 24px), 0 calc(100% - 24px)
      );
    }

    .lotops-modern-preview::after {
      left: 92px;
    }

    .lotops-ticket-stub {
      width: 104px;
      padding: 15px 8px 15px 14px;
      gap: 7px;
    }

    .lotops-ticket-stub span {
      font-size: 16px;
    }

    .lotops-ticket-barcode {
      width: 30px;
      height: 82px;
      border-width: 2px;
    }

    .lotops-ticket-main {
      margin-left: 6px;
      padding: 18px 14px 14px;
    }

    .lotops-ticket-meta {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .lotops-ticket-serial {
      font-size: 16px;
    }

    .lotops-ticket-number {
      font-size: 34px;
    }
  }

  .lotops-modern-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 13px;
  }

  .lotops-modern-manual {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .lotops-modern-result {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 12px;
    background: #ecfdf5;
    border-color: #bbf7d0;
  }

  .lotops-modern-result strong {
    color: #047857;
  }

  .lotops-modern-result span {
    color: #065f46;
    font-size: 13px;
    font-weight: 800;
  }

  .lotops-shift-required {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    border: 1px solid #fed7aa;
    border-radius: 18px;
    background: #fff7ed;
    padding: 18px;
  }

  .lotops-shift-required h3 {
    margin: 10px 0 4px;
    color: #9a3412;
  }

  .lotops-shift-required p {
    color: #c2410c;
  }

  .lotops-confirm-overlay {
    position: fixed;
    inset: 0;
    z-index: 1200;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    background: rgba(15, 23, 42, 0.52);
  }

  .lotops-confirm-modal {
    width: min(460px, 100%);
    border-radius: 20px;
    background: #ffffff;
    box-shadow: 0 28px 70px rgba(15, 23, 42, 0.32);
    padding: 22px;
  }

  .lotops-confirm-modal h4 {
    margin: 0 0 8px;
    color: #0f172a;
    font-size: 20px;
    font-weight: 950;
  }

  .lotops-confirm-modal p {
    margin: 0 0 14px;
    color: #475569;
    line-height: 1.5;
  }

  .lotops-confirm-check {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #334155;
    font-size: 13px;
    font-weight: 850;
  }

  .lotops-confirm-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 9px;
    margin-top: 18px;
  }

  @media (max-width: 820px) {
    .lotops-modern-scan-head,
    .lotops-modern-actions,
    .lotops-shift-required {
      flex-direction: column;
    }
    .lotops-modern-scan-grid,
    .lotops-modern-manual {
      grid-template-columns: 1fr;
    }
    .lotops-modern-shift,
    .lotops-modern-primary,
    .lotops-modern-secondary,
    .lotops-modern-link {
      width: 100%;
    }
  }
`;

export default LotteryOperationsTicketScan;
